import {
  ColGroupDef,
  Column,
  ColumnGroup,
  GridApi,
  IRowNode,
  RowSpanParams,
} from "@ag-grid-community/core";
import { parseISO } from "date-fns";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableColumn } from "gen/model/genericTableColumn";
import { GenericTableRow } from "gen/model/genericTableRow";
import { isEqual, isNumber, startCase } from "lodash-es";
import { getAgClientSideRowModel } from "shared/components/table/ag/AgGridUtils";
import {
  DataTableColumnContext,
  DataTableContext,
} from "shared/components/table/data/DataTableContext";
import {
  DataTableAgHeaderComponentParams,
  RenderCellValueProps,
} from "shared/components/table/data/DataTableTypes";
import { buildDataTableColumnContext } from "shared/components/table/data/internal/DataTableContextBuilders";
import {
  formatIsoDatetimeToDisplay,
  formatIsoDateToDisplay,
} from "shared/utils/DateUtils";
import {
  decimalOrZero,
  formatDecimalForTable,
} from "shared/utils/DecimalUtils";
import { formatPercentageForTable } from "shared/utils/formattingUtils";
import { formatMoneyStringForTable } from "shared/utils/MoneyUtils";
import { isTruthy } from "shared/utils/utils";
import { match, P } from "ts-pattern";

export const isDataTableSensitive = (
  columnContext: DataTableColumnContext,
  cell?: GenericTableCell,
) =>
  columnContext.column.sensitivity === "SENSITIVE" ||
  cell?.sensitivity === "SENSITIVE";

export const isGenericTableCellTextOnly = (cell: GenericTableCell) =>
  cell.value?.type !== "boolean" &&
  !cell.badge &&
  !cell.form_field &&
  !cell.error &&
  !cell.status &&
  !cell.tooltip &&
  !cell.sensitivity &&
  !cell.metadata;

export const genericColumnFromAgColGroupDef = (
  def: ColGroupDef | undefined | null,
): GenericTableColumn | undefined =>
  (
    def?.headerGroupComponentParams as
      | DataTableAgHeaderComponentParams
      | undefined
  )?.column;

export const isGenericTableColumn = (agColumn: Column) => {
  const params: DataTableAgHeaderComponentParams | undefined =
    agColumn.getColDef().headerComponentParams;
  return params && "column" in params;
};

export const getRootGenericTableColumnFromAgColumnGroup = (
  columnGroup: ColumnGroup | null,
): GenericTableColumn | undefined => {
  let topParent = columnGroup;
  while (topParent?.getParent()) {
    topParent = topParent.getParent();
  }
  return genericColumnFromAgColGroupDef(topParent?.getColGroupDef());
};

export const valueForCell = (
  cell: GenericTableCell | null | undefined,
  percentAsDecimal = false,
) => {
  if (!cell) {
    return "";
  }
  switch (cell.value?.type) {
    case "text":
      return cell.value.text;
    case "decimal":
      return cell.value.value;
    case "integer":
      return cell.value.value;
    case "percent":
      return percentAsDecimal
        ? decimalOrZero(cell.value.value).div(100).valueOf()
        : cell.value.value;
    case "money":
      return cell.value.amount;
    case "boolean":
      return cell.value.value;
    case "date":
      return cell.value.date;
    case "datetime":
      return cell.value.datetime;
  }
};

export const formattedValueForCell = (
  tableContext: DataTableContext,
  cell: GenericTableCell | null | undefined,
) => {
  switch (cell?.value?.type) {
    case "text":
      return cell.value.text;
    case "decimal":
      return formatDecimalForTable(cell.value.value);
    case "integer":
      return cell.value.value.toString();
    case "percent":
      return formatPercentageForTable(cell.value.value);
    case "money":
      return formatMoneyStringForTable(tableContext, cell.value.amount);
    case "boolean":
      return startCase(cell.value.value.toString());
    case "date":
      return formatIsoDateToDisplay(cell.value.date);
    case "datetime":
      return formatIsoDatetimeToDisplay(cell.value.datetime);
  }
};

export const valueForCellSort = (
  cell: GenericTableCell | null | undefined,
): string | number | null => {
  if (!cell) {
    return null;
  }
  if (cell.sort_key) {
    return cell.sort_key;
  }
  const cellValue = match(cell.value)
    .with({ type: "text" }, (v) => v.text)
    .with({ type: "decimal" }, (v) => Number(v.value))
    .with({ type: "integer" }, (v) => v.value)
    .with({ type: "percent" }, (v) => Number(v.value))
    .with({ type: "money" }, (v) => Number(v.amount))
    .with({ type: "boolean" }, (v) => v.value.toString())
    .with({ type: "date" }, (v) => v.date)
    .with({ type: "datetime" }, (v) => v.datetime)
    .with({ type: "formula" }, () => null)
    .with(P.nullish, () => null)
    .exhaustive();
  if (isNumber(cellValue)) {
    return cellValue;
  }
  const badgeValue = cell.badge?.text;
  return [cellValue, badgeValue].filter(Boolean).join(" ");
};

export const valueForCellExport = (
  cell: GenericTableCell | undefined,
): string => {
  if (!cell) {
    return "";
  }
  let value = valueForCell(cell, true)?.toString() ?? "";
  // Only include badge for text cells, otherwise this breaks the excel export
  // because it expects only a number/date/etc.
  if (!cell.value || cell.value.type === "text") {
    const badgeValue = cell.badge?.text;
    if (value && badgeValue) {
      value = `${value} (${badgeValue})`;
    } else if (badgeValue) {
      value = badgeValue;
    }
  }
  return value;
};

export const valueForCellFilter = (cell: GenericTableCell | undefined) => {
  if (cell?.value?.type === "date") {
    return cell.value.date ? parseISO(cell.value.date) : "";
  } else if (cell?.value?.type === "datetime") {
    return cell.value.datetime ? parseISO(cell.value.datetime) : "";
  }
  return valueForCellExport(cell);
};

export const valueForCellKeyCreator = (cell: GenericTableCell | undefined) =>
  valueForCellExport(cell);

export const cellValueForAggregation = (
  cell: GenericTableCell | undefined,
): string | null => {
  if (cell) {
    switch (cell.value?.type) {
      case "money":
        return cell.value.amount;
      case "integer":
        return cell.value.value.toString();
      case "decimal":
      case "percent":
        return cell.value.value;
    }
  }
  return "0";
};

export const iterateGenericTableRows = function* (
  rows: GenericTableRow[],
): Generator<GenericTableRow> {
  for (const row of rows) {
    yield row;
    if (row.child_rows) {
      yield* iterateGenericTableRows(row.child_rows);
    }
  }
};

export const iterateGenericTableColumns = function* (
  columns: GenericTableColumn[],
): Generator<GenericTableColumn> {
  for (const column of columns) {
    yield column;
    if (column.columns) {
      yield* iterateGenericTableColumns(column.columns);
    }
  }
};

export const iterateGenericTableCells = function* (
  rows: GenericTableRow[],
): Generator<GenericTableCell> {
  for (const row of iterateGenericTableRows(rows)) {
    for (const cell of Object.values(row.cells)) {
      yield cell;
    }
  }
};

export const rowSpan = (
  params: RowSpanParams<GenericTableRow, GenericTableCell>,
) => {
  const tableContext = params.context as DataTableContext;
  const { getRowSpanKeyForCell } = tableContext;
  if (!getRowSpanKeyForCell) {
    return 1;
  }
  if (!params.node?.data) {
    return 1;
  }

  const cell = params.node.data.cells[params.column.getColId()] as
    | GenericTableCell
    | undefined;
  if (!cell) {
    return 1;
  }

  const props = makeRenderCellValueProps(
    tableContext,
    params.column,
    params.node,
    cell,
  );

  const spanKey = getRowSpanKeyForCell(props);
  if (!spanKey) {
    return 1;
  }

  const rows: IRowNode<GenericTableRow>[] | null = getAgClientSideRowModel(
    params.api,
  ).getTopLevelNodes();
  if (!rows) {
    return 1;
  }

  const rowIndex = params.node.rowIndex!;

  const nextRowIndexWithDistinctKey = rows.findIndex((itRow, itRowIndex) => {
    if (itRowIndex <= rowIndex) {
      return false;
    }
    const itCell = itRow.data?.cells[params.column.getColId()];
    if (!itCell) {
      return true;
    }
    const itRowSpanKey = getRowSpanKeyForCell({
      ...props,
      cell: itCell,
      row: itRow,
    });
    return itRowSpanKey !== spanKey;
  });
  const numRowsBelow = rows.length - rowIndex;
  return nextRowIndexWithDistinctKey > -1
    ? nextRowIndexWithDistinctKey - rowIndex
    : numRowsBelow;
};

export const makeRenderCellValueProps = (
  tableContext: DataTableContext,
  column: Column,
  row: IRowNode<GenericTableRow>,
  cell: GenericTableCell,
): RenderCellValueProps => {
  const columnContext = buildDataTableColumnContext(tableContext, column);
  return {
    type: "data",
    tableContext,
    columnContext,
    row,
    cell,
  };
};

type IsCellSkippedFn = (props: {
  api: GridApi<GenericTableRow>;
  tableContext: DataTableContext;
  columnContext: DataTableColumnContext;
  row: IRowNode<GenericTableRow>;
  cell: GenericTableCell;
}) => boolean;

export const isCellSkipped: IsCellSkippedFn = ({
  api,
  tableContext,
  columnContext,
  row,
  cell,
}) => {
  const { getRowSpanKeyForCell } = tableContext;
  if (!getRowSpanKeyForCell) {
    return false;
  }
  const { columnId } = columnContext;
  const spanKey = getRowSpanKeyForCell({
    type: "data",
    tableContext,
    columnContext,
    row,
    cell,
  });
  const rowIndex = row.rowIndex;
  if (!spanKey || rowIndex === 0) {
    return false;
  }

  const rows: IRowNode<GenericTableRow>[] | null =
    getAgClientSideRowModel(api).getTopLevelNodes();

  if (!rows || rowIndex === null) {
    return false;
  }

  // Skip this cell if row above has matching cell with the same span key
  const rowAbove = rows.at(rowIndex - 1);
  const cellAbove = rowAbove?.data?.cells[columnId];
  const cellAboveSpanKey =
    rowAbove && cellAbove
      ? getRowSpanKeyForCell({
          type: "data",
          tableContext,
          columnContext,
          row: rowAbove,
          cell: cellAbove,
        })
      : undefined;
  return Boolean(cellAbove && cellAboveSpanKey && cellAboveSpanKey === spanKey);
};

export const getFirstInitialRowGroupColumn = (
  columns: GenericTableColumn[],
): GenericTableColumn | undefined => {
  for (const column of iterateGenericTableColumns(columns)) {
    if (column.initial_row_group) {
      return column;
    }
  }
};

export const getAllLeafChildrenAfterFilter = (
  node: IRowNode<GenericTableRow> | null | undefined,
): IRowNode<GenericTableRow>[] | undefined => {
  if (!node) {
    return undefined;
  }
  const children = node.childrenAfterFilter;
  return children?.flatMap((child) => [
    child,
    ...(getAllLeafChildrenAfterFilter(child) ?? []),
  ]);
};

export const getCellForRowGroup = (params: {
  value?: GenericTableCell | null;
  column?: Column<GenericTableCell>;
  node?: IRowNode<GenericTableRow> | null;
}) => {
  const cell = params.value;
  if (cell) {
    return cell;
  }
  const cells =
    getAllLeafChildrenAfterFilter(params.node)
      ?.map((node) => node.data?.cells[params.column!.getColId()])
      .filter(isTruthy) ?? [];
  const firstChildCell = cells.at(0);
  const isContiguous = cells
    .filter(Boolean)
    .every((cell) => isEqual(cell, firstChildCell));
  if (isContiguous) {
    return firstChildCell;
  }
};
